/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import type { HallowABFlag } from "@packages/ab";
import { useMixpanel } from "@packages/ab";
import { Button } from "@packages/ui/emotion";
import { useMemo } from "react";

import { LINKS } from "../../../constants";
import { useABLoadingWithTimeout } from "../../../hooks/useABLoadingWithTimeout";
import type { CTAProps } from "./CTA";
import { CTA } from "./CTA";

const disabledOverride: Interpolation = {
  opacity: 1,
};

type CtaWithABTestProps = {
  baseProps: CTAProps;
  controlProps?: Partial<CTAProps>;
  treatmentProps: Partial<CTAProps>;
  flag: HallowABFlag;
  treatmentValue?: "control" | "treatment" | "excluded";
  controlValue?: "control" | "treatment" | "excluded";
};

const Internal = ({
  baseProps,
  controlProps = baseProps,
  treatmentProps,
  flag,
  treatmentValue = "treatment",
  controlValue = "control",
}: CtaWithABTestProps) => {
  const { flags } = useMixpanel();
  const inTreatment = flags?.[flag]?.variant === treatmentValue;
  const inControl = flags?.[flag]?.variant === controlValue;

  const props = useMemo(
    () => ({
      ...baseProps,
      ...(inControl ? controlProps : {}),
      ...(inTreatment ? treatmentProps : {}),
    }),
    [baseProps, treatmentProps, inTreatment, inControl],
  );
  return <CTA {...props} />;
};

export const CtaWithABTest = (props: CtaWithABTestProps) => {
  const { isLoading } = useABLoadingWithTimeout();
  const t = useTranslations();
  if (isLoading)
    return (
      <Button css={disabledOverride} disabled size={props.baseProps.size}>
        {t(LINKS.tryHallowForFree.message)}
      </Button>
    );
  return <Internal {...props} />;
};
